import { Link, type ErrorComponentProps } from "@tanstack/react-router";
import { parseError } from "evlog";

import { PageDescription } from "#/components/PageDescription";
import { PageEyeBrow } from "#/components/PageEyeBrow";
import { PageHeader } from "#/components/PageHeader";
import { PageIntro } from "#/components/PageIntro";
import { PageShell } from "#/components/PageShell";
import { Button } from "#/components/ui/button";

function RouteFallbackPage({
  eyeBrow,
  title,
  description,
  actions,
}: {
  eyeBrow: string;
  title: string;
  description: string;
  actions: React.ReactNode;
}) {
  return (
    <PageShell>
      <PageIntro>
        <PageEyeBrow>{eyeBrow}</PageEyeBrow>
        <PageHeader>{title}</PageHeader>
        <PageDescription>{description}</PageDescription>
      </PageIntro>
      <div className="mt-8 flex flex-wrap justify-center gap-2">{actions}</div>
    </PageShell>
  );
}

export function DefaultRouteErrorComponent({ error, reset }: ErrorComponentProps) {
  const { message, why, fix } = parseError(error);
  const description = [message, why && why !== message ? why : null, fix].filter(Boolean).join(" ");

  return (
    <RouteFallbackPage
      eyeBrow="Unexpected error"
      title="Something went wrong"
      description={description}
      actions={
        <>
          <Button type="button" variant="outline" onClick={reset}>
            Try again
          </Button>
          <Link to="/" preload={false}>
            <Button type="button">Go home</Button>
          </Link>
        </>
      }
    />
  );
}

export function DefaultRouteNotFoundComponent() {
  return (
    <RouteFallbackPage
      eyeBrow="Nothing to see here"
      title="Page not found"
      description="The page you are looking for does not exist."
      actions={
        <Link to="/" preload={false}>
          <Button type="button">Go home</Button>
        </Link>
      }
    />
  );
}
